import { DateTime } from 'luxon';
import { Plan_ExercisePath, Plan_SubSlot } from '@cf-shared/data-model/fitness-plan';
import { dropAdjustmentsThatEmptyTheExercise } from '@cf-shared/data-model/plan-set-count';
import { Log_Workout, Log_WorkoutExercise, Log_WorkoutSet } from '@cf-shared/data-model/log-workout';
import { LogRecordState } from '@cf-shared/data-model/user-log';
import { reducedTargetWeightAfterDays } from '@cf-shared/fitness-constants';
import { deepClone } from '@cf-shared/deep-helpers';
import { daysSinceDate } from 'util/time';
import { maxBy } from 'lodash-es';
import { clearAutoregulationFields } from 'pages/workout-detail/autoregulation-resolve';

// Pure (Angular-free) layoff-restore logic for the Edit Workouts → Add Exercise path (#3321).
// Kept out of exercise-switching.ts — whose Angular-service imports can't load in the unit-test
// runner — so `findUsableLayoffBackup` can be unit-tested directly. See exercise-layoff-restore.test.ts.

// A backup is usable for a layoff restore only when it carries ESTABLISHED working progression
// (non-empty exercisePaths with a target_weight_kg) and a conclusion timestamp. Calibration/test
// snapshots (empty paths, or a path that is still a strength test with no target weight) are
// skipped, so we never re-seed a strength test.
function isUsableLayoffBackup(backup: Plan_SubSlot | undefined): boolean {
  return !!backup
    && !!backup.lastTimeConcluded_utc
    && Array.isArray(backup.exercisePaths)
    && backup.exercisePaths.length > 0
    && backup.exercisePaths.some(p => p.target_weight_kg != null);
}

/** The record of where ONE logged session left an exercise, best source first (df-5dbef4889250):
 *
 *  1. `planSubslotAfterSession` — the program entry as the session left it, taken at the end of
 *     every set conclusion. Present on sessions logged since it was added.
 *  2. Older STRENGTH-TEST sessions: `planSubslotBackup` (the state the session STARTED from, taken
 *     at the first set) with the tested intensity's path replaced by the `planPathBackup` of the
 *     LAST performed set after the first. That set's record is the path as the earlier sets of the
 *     session left it, so it already carries the test's result. It lacks only the last set's own
 *     progression and leaves the other intensities as they were at the start. See
 *     `testResultOverlay` for why it is limited to test sessions.
 *  3. Otherwise `planSubslotBackup` alone, as before.
 *
 *  A usable-looking answer from (1) wins outright. When (1) is absent or unusable, (2) and (3) are
 *  the same record with and without the overlay. The caller still applies `isUsableLayoffBackup`. */
export function layoffBackupCandidate(logExercise: Log_WorkoutExercise): Plan_SubSlot | undefined {
  const afterSession = logExercise.planSubslotAfterSession;
  if (isUsableLayoffBackup(afterSession)) {
    return afterSession;
  }
  const backup = logExercise.planSubslotBackup;
  if (!backup) {
    return undefined;
  }
  return testResultOverlay(backup, Array.isArray(logExercise.sets) ? logExercise.sets : []) ?? backup;
}

/** Source (2) of `layoffBackupCandidate`: the start-of-session copy of a strength-test session with
 *  the test's result laid over the tested path, or undefined when the session does not qualify.
 *
 *  It is limited to a session that looks like a strength test of THIS copy's exercise. Before
 *  #5507 an exercise could be switched after sets were logged without splitting them off, and the
 *  loader then re-took the start-of-session copy from the NEW exercise's program entry. The sets'
 *  records in such an entry belong to the OLD exercise, and laying one over the new copy would
 *  restore the old exercise's weights onto the new one. So the overlay needs both: the copy's own
 *  path at that intensity is a test with no target weight, and the FIRST performed set's record is
 *  that same test (same set type, still no weight). A session that started from a weighted path
 *  gains nothing it needs here: its start-of-session copy is already usable.
 *
 *  Known limit: a log set records no exercise, so a pre-#5507 switch from one fresh test to another
 *  fresh test of the same type still passes both checks, and the first exercise's result would be
 *  laid over the second. Nothing in the log tells the two apart. Such logs exist only from builds
 *  older than #5507 (on main 2026-09-23), and a restore ignores sessions past the retest threshold
 *  (about three weeks), so the window closes on its own. */
function testResultOverlay(backup: Plan_SubSlot, sets: Log_WorkoutSet[]): Plan_SubSlot | undefined {
  if (!Array.isArray(backup.exercisePaths)) {
    return undefined;
  }
  const firstSet = sets[0];
  const firstRecordedPath = firstSet && firstSet.state === LogRecordState.Done ? firstSet.planPathBackup : undefined;
  if (!firstRecordedPath || firstRecordedPath.target_weight_kg != null) {
    return undefined;
  }
  let lastRecordedPath: Plan_ExercisePath | undefined;
  for (let i = 1; i < sets.length; i++) {
    const set = sets[i];
    if (set && set.state === LogRecordState.Done && set.planPathBackup
      && set.planPathBackup.intensityLevel === firstRecordedPath.intensityLevel) {
      lastRecordedPath = set.planPathBackup;
    }
  }
  if (!lastRecordedPath) {
    return undefined;
  }
  const pathIndex = backup.exercisePaths.findIndex(p => p && p.intensityLevel === firstRecordedPath.intensityLevel);
  const startPath = pathIndex >= 0 ? backup.exercisePaths[pathIndex] : undefined;
  // a copy with no path at this intensity has nowhere to put the record (adding a lone path would
  // leave the restored entry missing every other intensity), and a copy whose path is not that
  // same weightless test does not describe the exercise these sets were performed on
  if (!startPath || startPath.target_weight_kg != null || startPath.nextWorkoutSetType !== firstRecordedPath.nextWorkoutSetType) {
    return undefined;
  }
  const overlaid = deepClone(backup);
  const path = deepClone(lastRecordedPath);
  // a question that set had open is not part of where the session left the exercise
  clearAutoregulationFields(path);
  overlaid.exercisePaths[pathIndex] = path;
  return overlaid;
}

/** Pick the most recent USABLE layoff backup for an exercise from its logged-workout history.
 *  Returns the backup plus the layoff reduction, or undefined when no usable backup exists or the
 *  gap warrants a fresh strength test (> the retest threshold). Pure / DB-agnostic (logs + the
 *  per-workout exercise accessor are injected) so it can be unit-tested without a database. */
export function findUsableLayoffBackup(
  workouts: Log_Workout[],
  exerciseId: string,
  todayLocal: DateTime,
  getExercises: (w: Log_Workout) => Log_WorkoutExercise[],
): { backup: Plan_SubSlot; reduction: number | undefined } | undefined {
  // collect every usable backup for this exercise across the user's logged workouts. we do NOT
  // gate on workout state: a planSubslotBackup is only written once the first set is concluded,
  // so its presence already implies a real progression event regardless of whether the workout
  // was later marked Done.
  const candidates: Plan_SubSlot[] = [];
  for (const workout of workouts) {
    const logExercise = getExercises(workout).find(e => e.exerciseId === exerciseId);
    const backup = logExercise && layoffBackupCandidate(logExercise);
    if (isUsableLayoffBackup(backup)) {
      candidates.push(backup);
    }
  }
  if (candidates.length < 1) {
    return undefined;
  }
  // most recent by the backup's own conclusion time (ISO-8601 UTC sorts chronologically;
  // do not rely on view-row order)
  const mostRecent = maxBy(candidates, b => b.lastTimeConcluded_utc);
  // candidates is non-empty and every entry passed isUsableLayoffBackup (which requires a
  // lastTimeConcluded_utc), so both are defined here — narrow for the type-checker.
  if (!mostRecent || !mostRecent.lastTimeConcluded_utc) {
    return undefined;
  }
  const reduction = reducedTargetWeightAfterDays(daysSinceDate(todayLocal, mostRecent.lastTimeConcluded_utc));
  // too long ago — a fresh strength test is the correct behavior, so do not restore
  if (reduction === 'retest') {
    return undefined;
  }
  return { backup: mostRecent, reduction };
}

/** Copy progression-bearing fields from a log backup onto a freshly-created subslot, PRESERVING
 *  the fresh slot's identity (slotId / startIndex / exercise_id) and defaultVolume_sets so
 *  placement and the #3320 orphan invariant are unaffected. intensities is taken WITH trainingLevel
 *  (getSlotPathIntensity indexes intensities[trainingLevel][...]) so the restored snapshot stays
 *  self-consistent. Mirrors the slot-tied vs exercise-tied split in design-docs/exercise-replacement.md. */
export function seedProgressionFromBackup(newSubslot: Plan_SubSlot, backup: Plan_SubSlot): void {
  // deep-clone: getWorkoutsForExerciseAsync returns live cached PouchDB docs
  const b = deepClone(backup);
  newSubslot.exercisePaths = b.exercisePaths;
  newSubslot.nextWorkoutIntensity = b.nextWorkoutIntensity;
  newSubslot.lastTimeConcluded_utc = b.lastTimeConcluded_utc;
  newSubslot.trainingLevel = b.trainingLevel;
  newSubslot.intensities = b.intensities;
  newSubslot.plateauWorkoutLog_id = b.plateauWorkoutLog_id;
  // the seeded paths keep their volumeAdjustment but land on the fresh slot's own base, often 1:
  // an adjustment that would leave it with no sets is reset (#6665)
  dropAdjustmentsThatEmptyTheExercise(newSubslot);
  // leaving rebuildPathsForIntensity = Initial would make rebuildPathsIfNeeded wipe the restored
  // paths and rebuild a strength test — clear it so the seeded progression is used as-is.
  delete newSubslot.rebuildPathsForIntensity;
}
